import { WeekDay } from '@angular/common';
import { Injectable } from '@angular/core';
import { HttpClient, HttpParams } from '@angular/common/http';

import * as moment from 'moment';
import { ConfigService } from './config.service';
import { Constants } from '../utilities/constants.util';
import { CostCenter } from 'src/shared/models/cost-center.model';
import { CostCenterService } from './cost-center.service';
import { ShiftBlock } from '../models/shift-block.model';
import { ShiftBlockService } from './shift-block.service';
import { ShiftRecord } from 'src/shared/models/shift-record.model';
import { ShiftRecordServiceInterface } from './shift-record-service.interface';
import { MiscUtil } from '../utilities/misc.util';

@Injectable({
    providedIn: 'root'
})
export abstract class ShiftRecordServiceBase implements ShiftRecordServiceInterface {

    protected _apiServer: string;
    protected _active?: ShiftRecord = null;
    protected _activePromise?: Promise<any> = null;

    constructor(
        protected _configService: ConfigService,
        protected _http: HttpClient,
        protected _costCenterService: CostCenterService,
        protected _shiftBlockService: ShiftBlockService) {	
        this._apiServer = this._configService.appSettings.webApiServer + "api/ShiftRecord/";
    }

    protected abstract getPreviousShiftBlockPromise(costCenter: CostCenter, shiftBlock: ShiftBlock, workflowTimeFacility: string);
    protected abstract prepareShiftRecordDetails(costCenter: CostCenter, shiftBlock: ShiftBlock, shiftRecord: ShiftRecord, workflowTime: string) : Promise<ShiftRecord>;
    
    public hasFacilityWorkflowData(facilityId: number): Promise<boolean> { 
        const params = new HttpParams().set('facilityId', facilityId.toString());

        return this._http.get<boolean>(`${this._apiServer}HasFacilityWorkflowData`, { params : params }).toPromise();
    }
    public hasCostCenterWorkflowData(costCenterId: number): Promise<boolean> { 
        const params = new HttpParams().set('costCenterId', costCenterId.toString());

        return this._http.get<boolean>(`${this._apiServer}HasCostCenterWorkflowData`, { params : params }).toPromise();
    }

    public setShiftRecordWithAssociated(shiftRecord: ShiftRecord): Promise<number> {
        return new Promise((resolve, reject) => {
            this.setShiftRecord(shiftRecord)
                .then((id) => {
                    if (id) {
                        shiftRecord.id = id;
                        resolve(shiftRecord.id);
                    } else {
                        resolve(null);
                    }
                    
            }, (errors) => {
                console.error(errors);
                reject(errors);
            });
        });
    }

    public setShiftRecordsWithAssociated(shiftRecords: ShiftRecord[]): Promise<number[]> {
        return new Promise((resolve, reject) => {
            this.setShiftRecords(shiftRecords)
                .then(ids => {
                    if (ids) {
                        for (let i = 0; i < shiftRecords.length; i++) {
                            shiftRecords[i].id = ids[i];
                        }
                        resolve(ids);
                    } 
                    else {
                        resolve(null);
                    }
                    
            }, (errors) => {
                console.error(errors);
                reject(errors);
            });
        });
    }

    public async getActive(costCenterId: number, shiftBlockId: number, workflowTimeFacility: string,previousShiftDate: string=""): Promise<ShiftRecord> {
        this._activePromise = new Promise((resolve, reject) => {
        return this._shiftBlockService.loadShiftBlockById(shiftBlockId).then((shiftBlock) => 
        {
        return this._costCenterService.getCostCenterForWorkflow(costCenterId, workflowTimeFacility, shiftBlock)
            .then((costCenter: CostCenter) => 
            {
                let promise = this.getPreviousShiftBlockPromise(costCenter, shiftBlock, workflowTimeFacility);
                return promise.then((previousShiftBlock) => 
                {
                    return Promise.all(this.getShiftRecordPromises(costCenter, workflowTimeFacility, shiftBlock, previousShiftBlock,previousShiftDate))
                        .then(records => 
                        {
                            this._active = records[0];
                            if (records[1]) {
                                this._active.prevShiftRecord = records[1];
                                this._active.prevShiftRecordId = records[1] ? records[1].id : null;
                            }
                            resolve(this._active);
                        }, (errors) => {
                        console.error(errors);
                        reject(errors)
                        });
                }, (errors) => {
                    console.error(errors);
                    reject(errors)
                });
            }, (errors) => {
            console.error(errors);
            reject(errors)
            });
        }, (errors) => {
            console.error(errors);
            reject(errors)
            });
        });

        return await this._activePromise;
    }

    private getShiftRecordPromises(costCenter: CostCenter, workflowTime: string, shiftBlock: ShiftBlock, previousShiftBlock: ShiftBlock,previousShiftDate:string="") : Promise<ShiftRecord>[] {
        let result = [];
        const workflowTimeFacility = MiscUtil.dateStartHourToFacilityTimeFormat(workflowTime,shiftBlock.startHour);
       
        result.push(this.getShiftRecord(costCenter, shiftBlock, workflowTimeFacility));
        if(previousShiftDate!=""){
            result.push(this.getShiftRecord(costCenter, previousShiftBlock, previousShiftDate));
            return result;
        }
        if (previousShiftBlock) {
            let previousWorkflowTime = moment(new Date(workflowTime).setHours(previousShiftBlock.startHour));
            let isUpdated = true;
            if ((shiftBlock.isWeekend || shiftBlock.isSaturday || shiftBlock.isSunday) && !shiftBlock.isWeekday) {
                // saturday open
                if(shiftBlock.isSaturday && previousShiftBlock.isWeekday){
                  // add -1 days 
                  previousWorkflowTime.add(-1,'days')
                }
                // sunday open 
               else if(shiftBlock.isSunday && previousShiftBlock.isWeekday){
                    // add -2 days
                    previousWorkflowTime.add(-2,'days');
                }
                else if(shiftBlock.isWeekend && !previousShiftBlock.isWeekend){
                    previousWorkflowTime.add(-1,'days');
                }
                else if (shiftBlock.isWeekend &&
                    previousShiftBlock.isWeekend &&
                    previousWorkflowTime.toDate() > moment(workflowTime).toDate()){
                    previousWorkflowTime.add(-1, 'days');
                }
            }
            else{
                if(previousWorkflowTime.toDate()>moment(workflowTime).toDate() && 
                (previousShiftBlock.isWeekend || previousShiftBlock.isSunday) && shiftBlock.isWeekday){
                    previousWorkflowTime.add(-1,'days');
                }
                else if(previousWorkflowTime.toDate()>moment(workflowTime).toDate() &&
                (previousShiftBlock.isWeekend || previousShiftBlock.isSaturday) && shiftBlock.isWeekday){
                    previousWorkflowTime.add(-2,'days');
                }
                else if(previousWorkflowTime.toDate()>moment(workflowTime).toDate() &&
                 (previousShiftBlock.isWeekday && shiftBlock.isWeekday)  && moment(workflowTime).weekday()!=WeekDay.Monday){
                    previousWorkflowTime.add(-1,'days');
                }
                else if(previousWorkflowTime.toDate()>moment(workflowTime).toDate() &&
                 (previousShiftBlock.isWeekday && shiftBlock.isWeekday) && moment(workflowTime).weekday()==WeekDay.Monday){
                    previousWorkflowTime.add(-3,'days');
                }
            }
            result.push(this.getShiftRecord(costCenter, previousShiftBlock, previousWorkflowTime.format(Constants.FacilityTimeFormat)));
        }
        return result;
    }

    clearActive() {
        this._active = null;
        this._activePromise = null;
    }

    // Return Hydrated ShiftRecord, don't make extra API Calls
    getShiftRecord(costCenter: CostCenter, shiftBlock: ShiftBlock, workflowTimeFacility: string) : Promise<ShiftRecord> {
        if (!shiftBlock) {
            return;
        }

        return new Promise((resolve, reject) => {
            const params = new HttpParams().set('shiftBlockId', shiftBlock.id.toString())
                                           .set('workflowTimeFacility', workflowTimeFacility);

            this._http.get<ShiftRecord>(`${this._apiServer}GetShiftRecord`, { params : params }).toPromise<ShiftRecord>()
                .then(async (shiftRecord: ShiftRecord) => {                      
                    shiftRecord = await this.prepareShiftRecordDetails(costCenter, shiftBlock, shiftRecord, workflowTimeFacility);

                    resolve(shiftRecord);
            });
        });
    }

    getShiftRecordById(shiftRecordId: number): Promise<ShiftRecord> {
        const params = new HttpParams().set('shiftRecordId', shiftRecordId.toString());

        return this._http.get<ShiftRecord>(`${this._apiServer}GetShiftRecordById`, { params : params }).toPromise<ShiftRecord>();
    }    

    protected prepareShiftRecord (
        costCenter: CostCenter, 
        shiftBlock: ShiftBlock, 
        shiftRecord: ShiftRecord, 
        workflowTimeFacility: string): ShiftRecord {
        let preparedShiftRecord = new ShiftRecord();

        preparedShiftRecord.id = 0;
        preparedShiftRecord.workflowTimeFacility = workflowTimeFacility;

        if (shiftRecord) {
            preparedShiftRecord = { ...shiftRecord };
        }

        preparedShiftRecord.costCenterId = costCenter.id;
        preparedShiftRecord.costCenter = costCenter;
        preparedShiftRecord.shiftBlockId = shiftBlock.id;
        preparedShiftRecord.shiftBlock = shiftBlock;
        preparedShiftRecord.staffingPlans = [];
        preparedShiftRecord.measureRecords = [];
        preparedShiftRecord.varianceReasonRecords = [];

        return preparedShiftRecord;
    }    

    private setShiftRecord(shiftRecord: ShiftRecord) : Promise<number> {
        return this._http.post<number>(`${this._apiServer}SetShiftRecord`, JSON.stringify(shiftRecord)).toPromise();
    }

    private setShiftRecords(shiftRecords : ShiftRecord[]): Promise<number[]> {
        return this._http.post<number[]>(`${this._apiServer}SetShiftRecords`, JSON.stringify(shiftRecords)).toPromise();
    }
}
